import { useState, useEffect, useMemo } from 'react';
import { useDebounce } from 'react-use-v13';
import _ from 'lodash';
import { IndexedInteractionStepResponse } from '@scale/shared/types/chat';
import { Button, Typography, SelectMenu, Tooltip, Divider, Checkbox, Chip } from '@scale/scaleui';
import { Transition } from '@headlessui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { CustomFilterTypes } from '@scale/shared/taskTemplate/helpers';
import { BenchmarkTutorialResponse } from '@scale/shared/types/benchmarks/tutorial';
import MarkdownToolbar from 'react-scale/components/scalers/tasks/ThreadedInteraction/components/editor/toolbar/MarkdownToolbar';
import MarkdownEditorContext from 'react-scale/components/scalers/tasks/ThreadedInteraction/components/editor/MarkdownEditorContext';
import PlainTextEditorContext from 'react-scale/components/scalers/tasks/ThreadedInteraction/components/editor/PlainTextEditorContext';
import {
  Editor,
  EditorContainer,
} from 'react-scale/components/scalers/tasks/ThreadedInteraction/components/editor';
import { ResponseTemplatePath } from './ResponseTemplatePath';

export interface ResponseTemplatePopperMetadata {
  stepIndex?: number;
  // Provided for per model response templating
  candidateId?: string;
  // Provided for per field templating
  fieldId?: string;
  // Whether the field is a text collection number field
  numberFieldScale?: {
    min: number;
    max: number;
  };
}

export interface ResponseTemplateVariable {
  label: string;
  previewValue?: string;
}
export interface ResponseTemplateStepProps {
  responseIndex: number;
  title: string;
  subtitle: string;
  paths: ResponseTemplatePath[];
  response: IndexedInteractionStepResponse;
  updateResponse: (path: string, value: any) => void;
  variables?: ResponseTemplateVariable[];
  isPopper?: boolean;
  numberFieldScale?: { min: number; max: number; scaleToDataBank: boolean };
  enableMarkdown?: boolean;
  enableTutorialFeedback?: boolean;
  stepTutorial?: BenchmarkTutorialResponse;
  setStepTutorial?: (stepTutorial: BenchmarkTutorialResponse) => void;
}

const CUSTOM_TEXT_PLACEHOLDER = 'Fixed value to use for all benchmarks';

const DATA_BANK_RATING_SCALE = {
  min: 1,
  max: 5,
};

/**
 * For ResponseTemplating, we modify Chat responses by directly modifying the response JSON using _.set and _.get
 *
 * Since modelIds can contain characters like . and :, we need to escape them to avoid issues with _.set and _.get
 */
export function escapeModelId(modelId: string) {
  return `["${modelId}"]`;
}

/**
 * Given a string containing LiquidJS variables, returns an array of all variable segments
 *
 * Variable segments may be simple like {{ variable }} or more complex with filters like {{ variable | minWith: variable2 }}
 */
export const extractVariableSegmentsFromString = (str: string) => {
  const regex = /\{\{[^}]+\}\}/g; // Regular expression to match LiquidJS variables
  const matches = str.match(regex);
  return matches || []; // Return the matches or an empty array if no matches are found
};

/**
 * Given an array of variables, returns a variable segment
 *
 * Case where variables is longer than 1 is primarily used for Data Bank rating fields, where multiple rating fields are mapped to one response in the project-specific response template
 * We currently just take the minimum value across all rating fields and scale it to the min and max values for the field, though if there are other use cases we can add more options
 */
export const stringifyVariableSegment = (
  variables: string[],
  scaleToOptions?: {
    min: number;
    max: number;
    scaledMin: number;
    scaledMax: number;
  },
) => {
  if (variables.length === 0) return '';
  const suffix = scaleToOptions
    ? `| ${CustomFilterTypes.scaleTo}: ${scaleToOptions.min}, ${scaleToOptions.max}, ${scaleToOptions.scaledMin}, ${scaleToOptions.scaledMax}`
    : '';
  if (variables.length === 1) return `{{ ['${variables[0]}'] ${suffix} }}`;
  return `{{ ${variables
    .map(v => `['${v}']`)
    .join(` | ${CustomFilterTypes.minWith}: `)} ${suffix} }}`;
};

/**
 * Given a string containing a variable segment, extracts the individual variables used and the scaledToOptions
 *
 * Ex input: {{ ['Safety.ratingValueA'] | minWith: ['Factuality.ratingValueA'] | scaleTo: 1, 5, 1, 7 }}
 * Ex output: { variables: ['Safety.ratingValueA', 'Factuality.ratingValueA'], scaledToOptions: { min: 1, max: 5, scaledMin: 1, scaledMax: 7 } }
 */
export const parseVariableSegment = (str: string) => {
  if (!str) return null;

  // Isolate the part of the string containing the variables
  const variablesPart = str.match(
    new RegExp('\\{\\{(.+?)(?:\\| ' + CustomFilterTypes.scaleTo + ':|\\}\\})'),
  )?.[1];
  if (!variablesPart) return null;

  // Split the string by known separators to isolate variables
  const splitParts = variablesPart
    .split(` | ${CustomFilterTypes.minWith}: `)
    .map(part => part.trim());

  const variables: string[] = [];

  // Extracting variables from each part
  splitParts.forEach(part => {
    const match = part.match(/(?:\[\s*')?([^'\]]+)(?:'\s*\])?/);
    if (match) variables.push(match[1]);
  });

  // Extracting min and max values, if they exist
  const minMaxPattern = new RegExp(CustomFilterTypes.scaleTo + ': (\\d+), (\\d+), (\\d+), (\\d+)');
  const minMaxMatch = minMaxPattern.exec(str);
  const min = minMaxMatch ? parseInt(minMaxMatch[1]) : undefined;
  const max = minMaxMatch ? parseInt(minMaxMatch[2]) : undefined;
  const scaledMin = minMaxMatch ? parseInt(minMaxMatch[3]) : undefined;
  const scaledMax = minMaxMatch ? parseInt(minMaxMatch[4]) : undefined;

  if ([min, max, scaledMin, scaledMax].some(v => v === undefined)) {
    return { variables };
  } else {
    return {
      variables,
      scaledToOptions: {
        min: min as number,
        max: max as number,
        scaledMin: scaledMin as number,
        scaledMax: scaledMax as number,
      },
    };
  }
};

/**
 * Given a string and an array of variables to remove, removes the variables from the string
 */
export const pruneVariablesFromString = (str: string, variablesToRemove: string[]) => {
  let responseString = str;
  variablesToRemove.forEach(variableToRemove => {
    extractVariableSegmentsFromString(str).forEach(variableSegment => {
      const { variables, scaledToOptions } = parseVariableSegment(variableSegment) ?? {};
      if (variables?.includes(variableToRemove)) {
        const newVariables = variables.filter(v => v !== variableToRemove);
        responseString = responseString.replace(
          variableSegment,
          stringifyVariableSegment(newVariables, scaledToOptions),
        );
      }
    });
  });
  return responseString;
};

/**
 * Given a string, returns an array of all variables used in the string
 */
export const extractVariablesFromString = (str: string) => {
  return _.flatten(
    extractVariableSegmentsFromString(str).map(variableSegment => {
      return parseVariableSegment(variableSegment)?.variables ?? [];
    }),
  )
    .map(variable => variable.trim())
    .filter(variable => variable !== '');
};

/**
 * Given a string, determines if the string is just a single variable segment and nothing else
 *
 * Ex: "{{ variableA }}" => true but "Hello {{ variableA }}" => false
 */
export const isVariableSegment = (str: string) => {
  const extracted = extractVariableSegmentsFromString(str);
  return extracted.length === 1 && extracted[0] === str;
};

export function ResponseTemplateStep({
  responseIndex,
  title,
  subtitle,
  paths,
  response,
  updateResponse,
  variables,
  isPopper = false,
  numberFieldScale,
  enableMarkdown = false,
  enableTutorialFeedback = false,
  stepTutorial,
  setStepTutorial,
}: ResponseTemplateStepProps) {
  return (
    <div className="flex flex-col gap-2 mx-1 bg-neutral-0 border border-neutral-200 rounded">
      <div className="flex flex-col">
        <div className="flex flex-col px-4 bg-neutral-100 pt-1 pb-2">
          <Typography.Subtitle3>{title}</Typography.Subtitle3>
          {enableTutorialFeedback && stepTutorial && setStepTutorial && (
            <div>
              <Button
                onClick={() => {
                  setStepTutorial(stepTutorial);
                }}
              >
                Test{' '}
              </Button>
            </div>
          )}
          <Typography.Subtitle2>{subtitle?.replace(/['"]+/g, '')}</Typography.Subtitle2>
          {!paths.length && <Typography.Subtitle3 className="m-2">N/A</Typography.Subtitle3>}
        </div>
        <Divider />
      </div>
      <div className="space-y-3">
        {paths.map((path, pathIdx) => {
          return (
            <ResponseTemplateStepPath
              key={`${responseIndex}-${path.path}`}
              responseIndex={responseIndex}
              path={path}
              response={response}
              updateResponse={updateResponse}
              isPopper={isPopper}
              variables={variables}
              numberFieldScale={numberFieldScale}
              enableMarkdown={enableMarkdown}
              pathIdx={pathIdx}
            />
          );
        })}
      </div>
    </div>
  );
}

interface ResponseTemplateStepPathProps {
  responseIndex: number;
  path: ResponseTemplatePath;
  response: IndexedInteractionStepResponse;
  updateResponse: (path: string, value: any) => void;
  isPopper?: boolean;
  variables?: ResponseTemplateVariable[];
  numberFieldScale?: { min: number; max: number; scaleToDataBank: boolean };
  enableMarkdown?: boolean;
  pathIdx?: number;
}

type EditorType = 'milkdown' | 'plain-text';

// eslint-disable-next-line complexity
function ResponseTemplateStepPath({
  path,
  response,
  updateResponse,
  isPopper = false,
  variables,
  numberFieldScale,
  enableMarkdown = false,
  pathIdx = 0,
}: ResponseTemplateStepPathProps) {
  const pathValue = useMemo(() => {
    return _.get(response, path.path);
  }, [response, path.path]);
  const pathValueString = useMemo(() => {
    return !_.isString(pathValue) ? JSON.stringify(pathValue) : pathValue;
  }, [pathValue]);
  const selectableOptions = useMemo(() => {
    return (
      variables?.map(variable => ({
        id: `${variable.label}`,
        name: `${variable.label}`,
      })) ?? []
    );
  }, [variables]);
  const selectedVariableId = useMemo(() => {
    return selectableOptions.find(
      option => option.id === parseVariableSegment(pathValueString ?? '')?.variables?.[0],
    )?.id;
  }, [pathValueString, selectableOptions]);
  const [showVariablePicker, setShowVariablePicker] = useState(
    isVariableSegment(pathValueString ?? ''),
  );
  useEffect(() => {
    // Only sync showVariablePicker to true to account for cases like when autopopulate inserts variables, require explicit user action to set to false
    if (isVariableSegment(pathValueString ?? '')) {
      setShowVariablePicker(true);
    }
  }, [pathValueString]);

  const [localPathResponse, setLocalPathResponse] = useState(pathValueString ?? '');
  const variablesSelected = useMemo(() => {
    return extractVariablesFromString(String(pathValueString));
  }, [pathValueString]);
  const [editorType, setEditorType] = useState<EditorType>('plain-text');

  const markdownEditorComponent = (
    <MarkdownEditorContext initialContent={localPathResponse}>
      <EditorContainer>
        <MarkdownToolbar />
        <Editor
          content={localPathResponse}
          onContentChange={(content: string) => {
            setLocalPathResponse(content);
          }}
        />
      </EditorContainer>
    </MarkdownEditorContext>
  );

  const plainTextEditorComponent = (
    <PlainTextEditorContext initialContent={localPathResponse}>
      <EditorContainer>
        <Editor
          content={localPathResponse}
          placeholder={CUSTOM_TEXT_PLACEHOLDER}
          onContentChange={(content: string) => {
            setLocalPathResponse(content);
          }}
        />
      </EditorContainer>
    </PlainTextEditorContext>
  );

  const handleUpdateResponse = (rawValue: string) => {
    let value = rawValue;
    try {
      value = JSON.parse(value);
    } catch (e) {
      // Do nothing
    }
    if (path.parseAsPrimitive || _.isObject(value) || _.isString(value)) {
      updateResponse(path.path, value);
    } else {
      updateResponse(path.path, rawValue);
    }
  };

  // Sync the local value with the upstream
  useEffect(() => {
    setLocalPathResponse(pathValueString ?? '');
  }, [pathValueString]);

  // Sync the upstream with the local value
  useDebounce(
    () => {
      handleUpdateResponse(localPathResponse);
    },
    500,
    [localPathResponse],
  );

  return (
    <div>
      {pathIdx > 0 && <Divider />}
      <div className="p-2">
        <div className="flex flex-row justify-between mb-2">
          <div className="flex flex-col break-normal text-left gap-2">
            <div className="flex items-center">
              <Typography.Subtitle3>{path.label ?? path.path}</Typography.Subtitle3>
            </div>

            {path.expectsArray && (
              <Typography.Subtitle4>
                {`Path expects a list, insert variable as JSON parsable array. Ex: [${JSON.stringify(
                  path.expectedValues?.[0] ?? 'placeholder_value',
                )}, ...]`}
              </Typography.Subtitle4>
            )}
            {path.expectedSchema && (
              <Typography.Subtitle4>
                Expected Schema: {JSON.stringify(path.expectedSchema, null, 2)}
              </Typography.Subtitle4>
            )}
            {path.expectedValues && (
              <Typography.Subtitle4>
                Expected Values:{' '}
                {_.truncate(path.expectedValues.join(', '), {
                  length: 500,
                })}
              </Typography.Subtitle4>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-2">
          {!!selectableOptions?.length && (
            <>
              <Checkbox
                checked={!showVariablePicker}
                onChange={e => {
                  handleUpdateResponse('');
                  setShowVariablePicker(!e.target.checked);
                }}
                label={`Set a fixed value for all benchmarks`}
              />
              {showVariablePicker && (
                <>
                  {numberFieldScale ? (
                    <>
                      <Transition
                        show={variablesSelected.length > 1}
                        appear
                        enter="transition-all"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="transition-all"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                        as="div"
                        className={`flex flex-col gap-2`}
                      >
                        <Typography.Subtitle4>
                          {`Multiple variables selected. We'll use the minimum value across these variables, scaled to the min and max values for this number field`}
                        </Typography.Subtitle4>
                        <div>
                          {variablesSelected.map(variable => {
                            return <Chip key={variable} label={variable} className="m-1" />;
                          })}
                        </div>
                      </Transition>
                      <SelectMenu
                        options={selectableOptions}
                        selectedOptionId={variablesSelected}
                        setSelected={(id: string | string[]) => {
                          handleUpdateResponse(
                            stringifyVariableSegment(
                              id as string[],
                              numberFieldScale.scaleToDataBank
                                ? {
                                    min: DATA_BANK_RATING_SCALE.min,
                                    max: DATA_BANK_RATING_SCALE.max,
                                    scaledMin: numberFieldScale.min,
                                    scaledMax: numberFieldScale.max,
                                  }
                                : undefined,
                            ),
                          );
                        }}
                        renderValue={(selectedOptionId: string[]) => {
                          if (selectedOptionId.length === 0) return 'Empty';
                          return selectedOptionId.join(', ');
                        }}
                        floatStrategy={isPopper ? 'fixed' : 'absolute'}
                        multiple
                      />
                    </>
                  ) : (
                    <SelectMenu
                      options={selectableOptions}
                      selectedOptionId={selectedVariableId ?? ''}
                      setSelected={(id: string) => {
                        handleUpdateResponse(stringifyVariableSegment([id]));
                      }}
                      renderValue={(selectedOptionId: string) => {
                        if (selectedOptionId === '') return 'Empty';
                        return selectedOptionId;
                      }}
                      floatStrategy={isPopper ? 'fixed' : 'absolute'}
                    />
                  )}
                </>
              )}
            </>
          )}
          {(!selectableOptions?.length || !showVariablePicker) && (
            <>
              {isPopper && path.expectedValues ? (
                // TODO: only supports single select right now
                <SelectMenu
                  options={path.expectedValues.map(value => ({
                    id: String(value),
                    name: String(value),
                  }))}
                  selectedOptionId={
                    path.expectedValues
                      .map(v => String(v))
                      .includes(
                        path.expectsArray ? String(_.get(pathValue, '[0]')) : String(pathValue),
                      )
                      ? String(path.expectsArray ? _.get(pathValue, '[0]') : pathValue)
                      : ''
                  }
                  setSelected={(id: string) => {
                    handleUpdateResponse(path.expectsArray ? `["${id}"]` : id);
                  }}
                  renderValue={(selectedOptionId: string) => {
                    if (selectedOptionId === '') return 'Empty';
                    return selectedOptionId;
                  }}
                  floatStrategy={isPopper ? 'fixed' : 'absolute'}
                />
              ) : (
                <div style={{ overflowAnchor: 'none' }}>
                  <div className="flex gap-2 py-1">
                    {enableMarkdown && (
                      <Tooltip
                        side="top"
                        text="Toggle between markdown and plain text editors"
                        target={
                          <div
                            className={clsx(
                              'cursor-pointer z-10 mb-1 text-neutral-500 rounded-md h-[25px] flex justify-center items-center px-2 gap-1',
                              {
                                'text-primary-400 bg-primary-100': editorType === 'milkdown',
                              },
                            )}
                            onClick={() => {
                              setEditorType(editorType === 'milkdown' ? 'plain-text' : 'milkdown');
                            }}
                          >
                            <FontAwesomeIcon icon="pencil-ruler" /> Markdown
                          </div>
                        }
                      />
                    )}
                  </div>
                  {editorType === 'milkdown' ? (
                    <div onKeyDown={e => e.stopPropagation()}>{markdownEditorComponent}</div>
                  ) : (
                    plainTextEditorComponent
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
